import { createFileRoute } from '@tanstack/react-router'
import { AdminReports } from '@/components/admin/AdminReports'
import type { ReportTab, ReportsSearch } from '@/components/admin/reports/reportsRouteSearch'

export type { ReportTab, ReportsSearch } from '@/components/admin/reports/reportsRouteSearch'

// Valid tab IDs accepted via `?tab=` query param. Lets other admin surfaces
// deep-link straight to a specific tab (e.g. Menu Management → Costing tab
// links to Reports → Items so admins jump from cost setup to sales impact in
// one click). Unknown values fall back to the default tab in ReportsShell.
const VALID_REPORT_TABS = ['overview', 'daily', 'adjustments', 'hours', 'items', 'tables', 'day-close', 'refunds', 'fiscal', 'tax', 'staff', 'card-promos', 'deals', 'accounts', 'kitchen-flow'] as const

const LEGACY_ORDERS_TAB = 'orders'

function localDateISO(): string {
  const x = new Date()
  x.setHours(0, 0, 0, 0)
  const y = x.getFullYear()
  const m = String(x.getMonth() + 1).padStart(2, '0')
  const day = String(x.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function parseDayParam(value: unknown): string | undefined {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined
  const d = new Date(`${value}T12:00:00`)
  if (Number.isNaN(d.getTime())) return undefined
  return value
}

export const Route = createFileRoute('/admin/reports')({
  validateSearch: (search: Record<string, unknown>): ReportsSearch => {
    const raw = typeof search.tab === 'string' ? search.tab : undefined
    let tab: ReportTab | undefined
    if (raw === LEGACY_ORDERS_TAB) {
      tab = 'daily'
    } else if (raw && (VALID_REPORT_TABS as readonly string[]).includes(raw)) {
      tab = raw as ReportTab
    }

    let day = parseDayParam(search.day)
    if (raw === LEGACY_ORDERS_TAB && !day) {
      day = localDateISO()
    }

    const closeId =
      typeof search.closeId === 'string' && search.closeId.length > 0
        ? search.closeId
        : undefined

    const month =
      typeof search.month === 'string' && /^\d{4}-\d{2}$/.test(search.month)
        ? search.month
        : undefined

    // `?order=<uuid>` — Daily Sales drill auto-opens this order (invoice search
    // deep-link). Accepts any non-empty token; OrdersBrowserTab only opens it
    // if it matches a loaded order for the day, so a stale id is harmless.
    const order =
      typeof search.order === 'string' && search.order.length > 0
        ? search.order
        : undefined

    // F-RPT-N30 — Orders browser facets. Anything malformed is dropped; the
    // backend re-validates every value, so this is only tidiness.
    const str = (v: unknown) => (typeof v === 'string' && v.trim() !== '' ? v.trim() : undefined)
    const ot = str(search.ot)
    const payRaw = str(search.pay)
    // F-MM-N24 added gift_card. Without it this gate silently rewrote the
    // facet to undefined at the router boundary, so the Tender mix "View
    // orders" drill on a gift row listed the entire day instead of the four
    // bills paid by card — the one query a guest disputing their balance
    // actually needs. The backend orders browser accepts the same four.
    const pay =
      payRaw === 'cash' ||
      payRaw === 'card' ||
      payRaw === 'online' ||
      payRaw === 'gift_card' ||
      payRaw === 'house_account' ||
      payRaw === 'staff_meal' ||
      payRaw === 'tab'
        ? payRaw
        : undefined
    const sub = pay === 'online' ? str(search.sub) : undefined
    const stRaw = str(search.st)
    const st =
      stRaw === 'completed' || stRaw === 'credit_noted' || stRaw === 'cancelled' || stRaw === 'open'
        ? stRaw
        : undefined
    const sort = str(search.sort)
    const dirRaw = str(search.dir)
    const dir = dirRaw === 'asc' || dirRaw === 'desc' ? dirRaw : undefined

    const preset = str(search.preset)
    const from = str(search.from)
    const to = str(search.to)

    const out: ReportsSearch = {}
    if (tab) out.tab = tab
    if (preset) out.preset = preset
    if (from) out.from = from
    if (to) out.to = to
    if (ot) out.ot = ot
    if (pay) out.pay = pay
    if (sub) out.sub = sub
    if (st) out.st = st
    if (sort) out.sort = sort
    if (dir) out.dir = dir
    if (day) out.day = day
    if (order) out.order = order
    if (closeId) out.closeId = closeId
    if (month) out.month = month
    // `?day=YYYY-MM-DD` alone should land on Daily Sales (drill open).
    if (day && !out.tab) out.tab = 'daily'
    // `?closeId=<uuid>` or `?month=YYYY-MM` alone should land on Day Close.
    if ((closeId || month) && !out.tab) out.tab = 'day-close'
    return out
  },
  component: () => <AdminReports />,
})
